/**
 * @module PackageReturnStatusBadges
 * @purpose Render the two collection-request status dimensions (packageStatus, returnStatus) as badges; return badge hidden while 'none' (PRP §2 C10).
 * @depends collection-request-status.styles, collection-request-status.labels, react-i18next
 * @dependedOnBy CollectionRequestsView
 *
 * @rules
 * - State: none -- pure presentational component
 * - Errors: n/a
 */

import type { ReactElement } from 'react'
import { useTranslation } from 'react-i18next'
import type { PackageStatus, ReturnStatus } from '../../../types/collection-requests'
import { packageStatusColourFor, returnStatusColourFor } from './collection-request-status.styles'
import { packageStatusLabel, returnStatusLabel } from './collection-request-status.labels'

interface PackageReturnStatusBadgesProps {
  packageStatus: PackageStatus
  returnStatus: ReturnStatus
  stallReason?: string | null
  size?: 'sm' | 'md'
}

const SIZE_CLASSES: Record<'sm' | 'md', string> = {
  sm: 'px-2 py-0.5 text-xs',
  md: 'px-2.5 py-1 text-xs',
}

/**
 * Two-dimension status badge pair.
 *
 * @behaviour Always renders the package-stage badge; renders the return-stage
 * badge only when returnStatus !== 'none'. A stalled package badge carries
 * stallReason as its title/tooltip.
 * @errors n/a -- pure display, no data fetching
 */
export function PackageReturnStatusBadges({
  packageStatus,
  returnStatus,
  stallReason,
  size = 'md',
}: PackageReturnStatusBadgesProps): ReactElement {
  const { t } = useTranslation('cases')
  const packageTitle = packageStatus === 'stalled' ? (stallReason ?? undefined) : undefined
  const returnColour = returnStatusColourFor(returnStatus)

  return (
    <span className="inline-flex items-center gap-1.5">
      <span
        title={packageTitle}
        className={`inline-flex rounded font-medium ${packageStatusColourFor(packageStatus)} ${SIZE_CLASSES[size]}`}
      >
        {packageStatusLabel(t, packageStatus)}
      </span>
      {returnStatus !== 'none' && returnColour !== null && (
        <span className={`inline-flex rounded font-medium ${returnColour} ${SIZE_CLASSES[size]}`}>
          {returnStatusLabel(t, returnStatus)}
        </span>
      )}
    </span>
  )
}
